import { Button, type MantineSize, type MantineThemeOverride } from '@mantine/core';
import styles from './exports.module.scss';
import { palette } from '@/styles/palette';
import { CONTROL_SIZES, getButtonFontSize } from '@/shared/utils/theme.utils';

const DEFAULT_SIZE: MantineSize = 'md';

const CALCULATED_CONTROL_SIZE = CONTROL_SIZES[DEFAULT_SIZE];
const CALCULATED_CONTROL_X_SPACING = CONTROL_SIZES[DEFAULT_SIZE];
const STYLES_LABEL = {
  fontWeight: styles.fontWeightSemibold,
  fontSize: styles.fontSizeMD,
  lineHeight: 1,
};

export const theme: MantineThemeOverride = {
  primaryColor: 'primary',
  primaryShade: 8,
  fontFamily: styles.fontFamilyPrimary,
  components: {
    AppShell: {
      styles: {
        main: {
          background: palette.primary[0],
        },
      },
    },
    Avatar: {
      defaultProps: {
        size: 'md',
      },
      styles: {
        root: {
          '--avatar-size-xs': CONTROL_SIZES.xs,
          '--avatar-size-sm': CONTROL_SIZES.sm,
          '--avatar-size-md': CONTROL_SIZES.md,
          '--avatar-size-lg': CONTROL_SIZES.lg,
          '--avatar-size-xl': CONTROL_SIZES.xl,
        },
      },
    },
    TextInput: {
      styles: {
        wrapper: {
          '--input-height-xs': CONTROL_SIZES.xs,
          '--input-height-sm': CONTROL_SIZES.sm,
          '--input-height-md': CONTROL_SIZES.md,
          '--input-height-lg': CONTROL_SIZES.lg,
        },
        label: {
          ...STYLES_LABEL,
        },
      },
    },
    Select: {
      styles: {
        label: {
          ...STYLES_LABEL,
        },
      },
    },
    InputBase: {
      styles: {
        label: {
          ...STYLES_LABEL,
        },
      },
    },
    NumberInput: {
      styles: {
        label: {
          ...STYLES_LABEL,
        },
      },
    },
    MultiSelect: {
      styles: {
        label: {
          ...STYLES_LABEL,
        },
      },
    },
    Textarea: {
      styles: {
        label: {
          ...STYLES_LABEL,
        },
      },
    },
    Switch: {
      defaultProps: {
        color: 'green.6',
      },
      styles: {
        label: {
          ...STYLES_LABEL,
          fontWeight: styles.fontWeightNormal,
        },
      },
    },
    InputWrapper: {
      styles: {
        label: {
          ...STYLES_LABEL,
        },
      },
    },
    Button: Button.extend({
      defaultProps: {
        size: DEFAULT_SIZE,
      },
      vars: (_, props) => {
        const { size = DEFAULT_SIZE } = props;
        return {
          root: {
            '--button-fz': getButtonFontSize(size as MantineSize),
          },
        };
      },
      styles: {
        root: {
          fontWeight: styles.fontWeightMedium,
          '--button-height-xs': CONTROL_SIZES.xs,
          '--button-height-sm': CONTROL_SIZES.sm,
          '--button-height-md': CONTROL_SIZES.md,
          '--button-height-lg': CONTROL_SIZES.lg,
          '--button-height-xl': CONTROL_SIZES.xl,
          '--button-height-compact-sm': CONTROL_SIZES.sm,
          '--button-height-compact-md': CONTROL_SIZES.md,
          '--button-height-compact-lg': CONTROL_SIZES.lg,
          '--button-height-compact-xl': CONTROL_SIZES.xl,
          '--button-padding-x': CALCULATED_CONTROL_X_SPACING,
          '--button-padding-x-xs': CALCULATED_CONTROL_X_SPACING,
          '--button-padding-x-sm': CALCULATED_CONTROL_X_SPACING,
          '--button-padding-x-md': CALCULATED_CONTROL_X_SPACING,
          '--button-padding-x-lg': CALCULATED_CONTROL_X_SPACING,
          '--button-padding-x-xl': CALCULATED_CONTROL_X_SPACING,
          '--button-padding-x-compact-xs': CALCULATED_CONTROL_X_SPACING,
          '--button-padding-x-compact-sm': CALCULATED_CONTROL_X_SPACING,
          '--button-padding-x-compact-md': CALCULATED_CONTROL_X_SPACING,
          '--button-padding-x-compact-lg': CALCULATED_CONTROL_X_SPACING,
          '--button-padding-x-compact-xl': CALCULATED_CONTROL_X_SPACING,
          '--button-radius': styles.borderRadius,
        },
      },
    }),
    Title: {
      styles: {
        root: {
          fontWeight: styles.fontWeightSemibold,
        },
      },
    },
    Modal: {
      styles: {
        inner: {
          left: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      },
    },
    ActionIcon: {
      defaultProps: {
        size: '',
      },
      styles: {
        root: {
          '--ai-size-xl': CALCULATED_CONTROL_SIZE,
        },
      },
    },
  },
  colors: {
    ...palette,
  },
  defaultRadius: 'md',
  spacing: {
    xxs: styles.spacingXXS,
    xs: styles.spacingXS,
    sm: styles.spacingSM,
    md: styles.spacingMD,
    lg: styles.spacingLG,
    xl: styles.spacingXL,
    xxl: styles.spacingXXL,
    basic: styles.spacingBasic,
  },
  breakpoints: {
    xs: styles.breakpointXS,
    sm: styles.breakpointSM,
    md: styles.breakpointMD,
    lg: styles.breakpointLG,
    xl: styles.breakpointXL,
  },
};
